import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { Field } from "redux-form/immutable"

import GridToggleField from "app/static/frontend/forms/components/GridToggleField"
import ReduxFormFieldWrapper from "app/static/frontend/forms/components/ReduxFormFieldWrapper"

// ARIA live region for announcing toggle state changes
const AriaLiveRegion = styled.div`
    position: absolute;
    left: -10000px;
    width: 1px;
    height: 1px;
    overflow: hidden;
`

// Enhanced wrapper for toggle options with proper ARIA support
const AccessibleToggleWrapper = styled.div`
    /* Ensure proper focus management */
    .grid-toggle {
        &[role="radiogroup"],
        &[role="group"] {
            /* Add visual focus indicator for keyboard navigation */
            &:focus-within {
                outline: 2px solid "#3b5998";
                outline-offset: 2px;
                border-radius: 4px;
            }
        }
    }

    /* Enhanced button styling for better accessibility */
    .gridtoggle-button {
        &:focus {
            outline: 2px solid "#3b5998";
            outline-offset: 2px;
            z-index: 1;
            position: relative;
        }

        &:focus-visible {
            outline: 2px solid "#3b5998";
            outline-offset: 2px;
            z-index: 1;
            position: relative;
        }

        /* Ensure sufficient color contrast for selected state */
        &.btn-primary {
            background-color: #1976d2;
            border-color: #1976d2;
            color: white;
        }
    }
`

/**
 * Enhanced GridToggleField component with improved accessibility for screen readers
 * This is the component that gets rendered by Redux Form Field
 */
const AccessibleGridToggleField = (props) => {
    const {
        input: { value, onChange },
        meta = {},
        accessibilityId,
        label,
        choices = [],
        multi,
        displayErrorWithoutTouch,
        tooltipText,
        showAsterisk,
        useAccessibleBinaryToggle,
        required,
        ...restProps
    } = props

    const [announcement, setAnnouncement] = useState("")
    const liveRegionId = `${accessibilityId}-live`
    const groupId = `${accessibilityId}-group`
    const labelId = `${accessibilityId}-label`
    const descriptionId = `${accessibilityId}-description`

    // Handle value changes for announcements
    const handleChange = (newValue) => {
        onChange(newValue)

        // Create comprehensive announcements for screen readers
        if (multi) {
            const selectedCount = newValue ? (Array.isArray(newValue) ? newValue.length : 0) : 0
            const selectedLabels = newValue
                ? newValue.map((val) => choices.find((choice) => choice.value === val)?.label || val).join(", ")
                : "none"
            setAnnouncement(`${label}: ${selectedCount} options selected. Selected: ${selectedLabels}.`)
        } else {
            const selectedLabel = newValue
                ? choices.find((choice) => choice.value === newValue)?.label || newValue
                : "none"
            setAnnouncement(`${label}: ${selectedLabel} selected.`)
        }
    }

    // Clear announcement after it's been read by screen readers
    useEffect(() => {
        if (announcement) {
            const timer = setTimeout(() => setAnnouncement(""), 2000)
            return () => clearTimeout(timer)
        }
    }, [announcement])

    // Generate accessible description
    const getAccessibleDescription = () => {
        const parts = []

        if (tooltipText) {
            parts.push(tooltipText)
        }

        if (multi) {
            parts.push("Multiple selection toggle group. Use arrow keys to navigate, Space to select multiple options.")
        } else {
            parts.push("Single selection toggle group. Use arrow keys to navigate, Space or Enter to select.")
        }

        if (choices.length > 0) {
            parts.push(`${choices.length} options available: ${choices.map((c) => c.label).join(", ")}.`)
        }

        return parts.join(" ")
    }

    return (
        <AccessibleToggleWrapper>
            <ReduxFormFieldWrapper
                accessibilityId={labelId}
                label={label}
                meta={meta}
                displayErrorWithoutTouch={displayErrorWithoutTouch}
                tooltipText={tooltipText}
                showAsterisk={showAsterisk}
                className="gridtoggle"
                {...restProps}
            >
                {/* Hidden description for screen readers */}
                <div id={descriptionId} style={{ display: "none" }}>
                    {getAccessibleDescription()}
                </div>

                {/* Enhanced GridToggle with proper ARIA attributes */}
                <div
                    id={groupId}
                    role={multi ? "group" : "radiogroup"}
                    aria-labelledby={labelId}
                    aria-describedby={descriptionId}
                    aria-required={showAsterisk || false}
                    aria-invalid={meta?.error && (displayErrorWithoutTouch || meta?.touched)}
                >
                    <GridToggleField
                        input={{ value, onChange: handleChange }}
                        meta={meta}
                        accessibilityId={accessibilityId}
                        choices={choices}
                        multi={multi}
                        displayErrorWithoutTouch={displayErrorWithoutTouch}
                        // Remove label and tooltipText since they're handled by the wrapper
                        label=""
                        tooltipText=""
                        // Explicitly pass through accessibility props
                        useAccessibleBinaryToggle={useAccessibleBinaryToggle}
                        required={required}
                        {...restProps}
                    />
                </div>

                {/* ARIA live region for dynamic announcements */}
                <AriaLiveRegion id={liveRegionId} role="status" aria-live="polite" aria-atomic="true">
                    {announcement}
                </AriaLiveRegion>
            </ReduxFormFieldWrapper>
        </AccessibleToggleWrapper>
    )
}

/**
 * Component for forced true fields with enhanced accessibility
 */
const renderAccessibleForcedTrueField = (props) => {
    const {
        meta: { error, warning, touched } = {},
        accessibilityId,
        banner,
        className,
        dataCy,
        displayErrorWithoutTouch,
        label,
        labelStyle,
        style,
        tooltipText,
        showAsterisk,
    } = props

    return (
        <ReduxFormFieldWrapper
            additionalClassNames={className}
            className="gridtoggle"
            dataCy={dataCy}
            displayErrorWithoutTouch={displayErrorWithoutTouch}
            error={error}
            label={label}
            labelStyle={labelStyle}
            style={style}
            tooltipText={tooltipText}
            touched={touched}
            warning={warning}
            accessibilityId={accessibilityId}
            banner={banner}
            showAsterisk={showAsterisk}
            // Add ARIA attributes for forced fields
            aria-readonly="true"
            aria-describedby={tooltipText ? `${accessibilityId}-description` : undefined}
        >
            {/* Hidden description for forced fields */}
            {tooltipText && (
                <div id={`${accessibilityId}-description`} style={{ display: "none" }}>
                    {tooltipText} This field is required and cannot be changed.
                </div>
            )}
        </ReduxFormFieldWrapper>
    )
}

/**
 * Enhanced ToggleField with improved accessibility
 * Automatically applies accessibility improvements to toggle fields
 */
export const AccessibleToggleField = (props) => {
    return props.allowChoices ? (
        <Field
            {...props}
            component={AccessibleGridToggleField}
            choices={
                props.choices || [
                    {
                        label: "Yes",
                        value: true,
                    },
                    {
                        label: "No",
                        value: false,
                    },
                ]
            }
            pillWidth={6}
        />
    ) : (
        <Field {...props} component={renderAccessibleForcedTrueField} />
    )
}

AccessibleToggleField.propTypes = {
    accessibilityId: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    allowChoices: PropTypes.bool,
    choices: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.any.isRequired,
            label: PropTypes.string.isRequired,
            disabled: PropTypes.bool,
            tip: PropTypes.string,
        }),
    ),
    multi: PropTypes.bool,
    displayErrorWithoutTouch: PropTypes.bool,
    tooltipText: PropTypes.string,
    showAsterisk: PropTypes.bool,
    // Accessibility props for binary toggle fields
    useAccessibleBinaryToggle: PropTypes.bool,
    required: PropTypes.bool,
}

AccessibleToggleField.defaultProps = {
    allowChoices: true,
    multi: false,
    displayErrorWithoutTouch: true,
}

export default AccessibleToggleField
